import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  InputNumber,
  Space,
  Card,
  Row,
  Col,
  message,
  Typography,
  Radio,
  Checkbox
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, SyncOutlined, CloseOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  createQuotation,
  updateQuotation,
  QUOTATION_TYPES,
  QUOTATION_TYPE_LABELS
} from '../../firebase/quotationService';
import { generateQuotationPDF } from './QuotationPDF';
import moment from 'moment';
import {
  createEnhancedLineItem,
  updateLineItemWithSmartDescription,
  resetToAutoDescription,
  regenerateAutoDescriptionsOnLoad
} from '../../utils/descriptionGenerator';
import UnifiedDateSection from '../Shared/UnifiedDateSection';
import AdditionalServicesSection from '../Shared/AdditionalServicesSection';
import { PAYMENT_TERMS, mapPaymentTermsForForm } from '../Shared/constants';
import { createEmptyDateEntry } from '../../utils/newDateUtils';
import { getDatesForPDF } from '../../utils/unifiedDateStorage';
import '../Form/AdminFormMobile.css';
import './QuotationForm.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

// Individual Mobile Line Item Component with stable keys and controlled inputs
const MobileLineItem = React.memo(({ item, index, onUpdate, onRemove, canRemove, onResetDescription, onAcceptUpdate, onDismissUpdate }) => {
  const handleDescriptionChange = React.useCallback((e) => {
    onUpdate(item.id, 'description', e.target.value);
  }, [item.id, onUpdate]);

  const handleQuantityChange = React.useCallback((value) => {
    onUpdate(item.id, 'quantity', value);
  }, [item.id, onUpdate]);

  const handleUnitPriceChange = React.useCallback((value) => {
    onUpdate(item.id, 'unitPrice', value);
  }, [item.id, onUpdate]);

  const handleRemove = React.useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);

  return (
    <div className="mobile-line-item">
      <div className="mobile-line-item-header">
        <span>Item #{index + 1}</span>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={handleRemove}
          disabled={!canRemove}
          size="small"
        />
      </div>
      
      <div className="mobile-line-item-field">
        <label>Description</label>
        <div style={{ position: 'relative' }}>
          <TextArea
            key={`desc-${item.id}`}
            value={item.description || ''}
            onChange={handleDescriptionChange}
            placeholder="Enter service description (multi-line supported)"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{
              borderColor: item.isManuallyEdited ? '#1890ff' : undefined,
              backgroundColor: item.isManuallyEdited ? '#f6ffed' : undefined
            }}
          />
          {/* Visual indicator for auto-generated vs manual descriptions */}
          {!item.isManuallyEdited && item.autoDescription && (
            <div style={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontSize: '10px',
              color: '#52c41a',
              backgroundColor: 'rgba(82, 196, 26, 0.1)',
              padding: '2px 4px',
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              gap: '2px'
            }}>
              <SyncOutlined style={{ fontSize: '8px' }} />
              Auto
            </div>
          )}
        </div>

        {/* Update prompt for manually edited descriptions */}
        {item.showUpdatePrompt && (
          <div style={{
            marginTop: 4,
            padding: '6px 8px',
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            <div style={{ marginBottom: 4 }}>
              <InfoCircleOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
              <span>Update available:</span>
            </div>
            <div style={{ 
              padding: '4px 6px', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '2px',
              fontSize: '11px',
              marginBottom: 4
            }}>
              "{item.autoDescription}"
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => onAcceptUpdate(item.id)}
                style={{ fontSize: '10px', height: '20px' }}
              >
                Accept
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onDismissUpdate(item.id)}
                style={{ fontSize: '10px', height: '20px' }}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Helper function controls */}
        <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Radio.Group
                size="small"
                value={item.width}
                onChange={(e) => onUpdate(item.id, 'width', e.target.value)}
              >
                <Radio.Button value="single">Single</Radio.Button>
                <Radio.Button value="double">Double</Radio.Button>
              </Radio.Group>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <InputNumber
                size="small"
                placeholder="Working Height"
                value={item.workingHeight}
                onChange={(value) => onUpdate(item.id, 'workingHeight', value)}
                min={2}
                max={15}
                step={0.1}
                addonAfter="m"
                style={{ flex: 1 }}
              />
              {/* Reset to auto button */}
              {item.isManuallyEdited && item.autoDescription && (
                <Button
                  size="small"
                  type="text"
                  icon={<SyncOutlined />}
                  onClick={() => onResetDescription(item.id)}
                  title="Reset to auto-generated description"
                  style={{ color: '#1890ff', fontSize: '10px' }}
                >
                  Reset
                </Button>
              )}
            </div>
            {/* Service checkboxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Checkbox
                size="small"
                checked={item.services?.transportation || false}
                onChange={(e) => onUpdate(item.id, 'services', { transportation: e.target.checked })}
                style={{ fontSize: '11px' }}
              >
                Transportation
              </Checkbox>
              <Checkbox
                size="small"
                checked={item.services?.assembly || false}
                onChange={(e) => onUpdate(item.id, 'services', { assembly: e.target.checked })}
                style={{ fontSize: '11px' }}
              >
                Assembly and dismantling
              </Checkbox>
              <Checkbox
                size="small"
                checked={item.services?.endorsement || false}
                onChange={(e) => onUpdate(item.id, 'services', { endorsement: e.target.checked })}
                style={{ fontSize: '11px' }}
              >
                Weekly inspection
              </Checkbox>
            </div>
          </Space>
        </div>
      </div>
      
      <div className="mobile-line-item-field">
        <label>Quantity</label>
        <InputNumber
          key={`qty-${item.id}`}
          min={1}
          value={item.quantity}
          onChange={handleQuantityChange}
          style={{ width: '100%' }}
        />
      </div>
      
      <div className="mobile-line-item-field">
        <label>Unit Price (SGD)</label>
        <InputNumber
          key={`price-${item.id}`}
          min={0}
          precision={2}
          value={item.unitPrice}
          onChange={handleUnitPriceChange}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          style={{ width: '100%' }}
        />
      </div>
      
      <div className="mobile-line-item-field">
        <label>Total</label>
        <div style={{ 
          padding: '8px 12px', 
          backgroundColor: '#f5f5f5', 
          borderRadius: '6px',
          fontWeight: 'bold',
          color: '#1890ff'
        }}>
          ${(item.total || 0).toFixed(2)}
        </div>
      </div>
    </div>
  );
});

// Mobile Line Items Container Component with stable rendering
const MobileLineItems = React.memo(({ lineItems, onUpdate, onRemove, onResetDescription, onAcceptUpdate, onDismissUpdate }) => {
  return (
    <div className="mobile-line-items">
      {lineItems.map((item, index) => (
        <MobileLineItem
          key={item.id}
          item={item}
          index={index}
          onUpdate={onUpdate}
          onRemove={onRemove}
          canRemove={lineItems.length > 1}
          onResetDescription={onResetDescription}
          onAcceptUpdate={onAcceptUpdate}
          onDismissUpdate={onDismissUpdate}
        />
      ))}
    </div>
  );
});

// Helper function to map customer info based on company field
const mapCustomerInfo = (linkedQuoteRequest) => {
  const company = linkedQuoteRequest.companyName;
  
  // If company is not "NA" and not empty, use company as name and customer as attention
  if (company && company !== 'NA' && company.trim() !== '') {
    return {
      companyName: company,
      attn: linkedQuoteRequest.customerName || ''
    };
  } else {
    // If company is "NA" or empty, use customer name as name and "NA" as attention
    return {
      companyName: linkedQuoteRequest.customerName || '',
      attn: 'NA'
    };
  }
};

// Helper function to parse products from quote request
const parseProductsFromQuoteRequest = (products, quoteRequestServices = null) => {
  if (!products || !Array.isArray(products)) {
    return [];
  }
  
  return products.map((product, index) => {
    let quantity = 1;
    let workingHeight = undefined;
    let width = 'single';
    let services = {
      transportation: false,
      assembly: false,
      endorsement: false
    };
    
    // Handle different product formats
    if (typeof product === 'string') {
      let productStr = product;
      
      // Parse string formats like "5x double width, 4m height"
      const quantityMatch = productStr.match(/^(\d+)x?\s*/);
      if (quantityMatch) {
        quantity = parseInt(quantityMatch[1]);
        productStr = productStr.replace(quantityMatch[0], '').trim();
      }
      
      // Extract width information
      if (productStr.toLowerCase().includes('double')) {
        width = 'double';
      } else if (productStr.toLowerCase().includes('single')) {
        width = 'single';
      }
      
      // Extract working height information
      const heightMatch = productStr.match(/(\d+(?:\.\d+)?)\s*m?\s*height/i);
      if (heightMatch) {
        workingHeight = parseFloat(heightMatch[1]);
      }
      
    } else if (typeof product === 'object' && product !== null) {
      // Handle object format like {quantity: 5, width: 'double', workingHeight: 4.5}
      if (product.quantity) {
        quantity = parseInt(product.quantity) || 1;
      }
      
      // Extract width from object
      if (product.width) {
        if (typeof product.width === 'string') {
          width = product.width.toLowerCase().includes('double') ? 'double' : 'single';
        } else {
          width = product.width;
        }
      }
      
      // Extract working height from object
      if (product.workingHeight !== undefined) {
        workingHeight = parseFloat(product.workingHeight);
      } else if (product.height !== undefined) {
        // Handle different height formats
        if (typeof product.height === 'string') {
          const heightMatch = product.height.match(/(\d+(?:\.\d+)?)/);
          if (heightMatch) {
            workingHeight = parseFloat(heightMatch[1]);
          }
        } else {
          workingHeight = parseFloat(product.height);
        }
      }
    }
    
    // Load services from quote request if available
    if (quoteRequestServices) {
      services = {
        transportation: quoteRequestServices.transportation || false,
        assembly: quoteRequestServices.assemblyDismantle || false, // Map from assemblyDismantle field
        endorsement: quoteRequestServices.endorsement || false
      };
    }
    
    // Create enhanced line item with auto-generated description using the description generator
    // This will automatically generate the detailed description pattern when workingHeight is available
    return createEnhancedLineItem({
      id: index + 1,
      description: '', // Let the description generator create the description
      quantity: quantity,
      unitPrice: 0,
      total: 0,
      workingHeight: workingHeight,
      width: width,
      services: services
    });
  });
};

const QuotationForm = ({ 
  quotation = null, 
  onSave = () => {},
  onCancel = () => {},
  linkedQuoteRequest = null
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const [lineItems, setLineItems] = useState([
    createEnhancedLineItem({ 
      id: 1, 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      total: 0,
      workingHeight: undefined,
      width: undefined
    })
  ]);
  const [additionalServices, setAdditionalServices] = useState({
    additionalTransport: { selected: true, price: 100 },
    additionalInstallation: { selected: true, price: 250 },
    stairsInstallation: { selected: true, price: 100 },
    stairsLoading: { selected: true, price: 30 },
    weeklyInspection: { selected: true, price: 100 }
  });
  const [quotationType, setQuotationType] = useState(QUOTATION_TYPES.PURCHASE);
  const [discount, setDiscount] = useState({ type: 'none', value: 0 }); // 'none', 'percentage', 'fixed'
  const [gst, setGst] = useState({ percentage: 0 }); // GST percentage, default 0%
  const [showTotalSegment, setShowTotalSegment] = useState(true); // Show total pricing details in PDF
  const [customAdditionalServices, setCustomAdditionalServices] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [formKey, setFormKey] = useState(Date.now());
  const [dateEntries, setDateEntries] = useState([]);

  // Mobile detection hook
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate new component key when switching between quotations
  useEffect(() => {
    // Only generate new keys when quotation ID actually changes
    const quotationId = quotation?.id || 'new';
    const newFormKey = `form-${quotationId}-${Date.now()}`;

    setFormKey(newFormKey);
  }, [quotation?.id]);

  // Initialize form with existing quotation data or linked quote request
  useEffect(() => {
    const initializeForm = () => {
      if (quotation) {
        // Use intelligent payment terms mapping for backward compatibility
        const paymentTermsMapping = mapPaymentTermsForForm(quotation.paymentTerms, quotation.customPaymentTerms);

        const formValues = {
          companyName: quotation.companyName,
          attn: quotation.attn,
          tel: quotation.tel,
          email: quotation.email,
          siteAddress: quotation.siteAddress,
          billingAddress: quotation.billingAddress,
          quotationType: quotation.quotationType,
          paymentTerms: paymentTermsMapping.paymentTerms,
          customPaymentTerms: paymentTermsMapping.customPaymentTerms,
          notes: quotation.notes,
          internalRemarks: quotation.internalRemarks
        };

        // Load unified dates if available
        if (quotation.dates && Array.isArray(quotation.dates) && quotation.dates.length > 0) {
          console.log('🔍 Loading unified dates from quotation:', quotation.dates);
          setDateEntries(quotation.dates);
        } else {
          // Create default date entry for existing quotations without unified dates
          console.log('🔍 No unified dates found, creating default entry');
          const defaultDates = [createEmptyDateEntry(quotation.quotationType)];
          setDateEntries(defaultDates);
        }

        // Update states and form values
        form.setFieldsValue(formValues);
        setQuotationType(quotation.quotationType);

        // Regenerate auto descriptions for existing line items on load
        const processedLineItems = regenerateAutoDescriptionsOnLoad(quotation.lineItems || []);
        setLineItems(processedLineItems);

        setAdditionalServices(quotation.additionalServices || additionalServices);

        // Load existing discount and GST data
        if (quotation.discount) {
          setDiscount(quotation.discount);
        }
        if (quotation.gst) {
          setGst(quotation.gst);
        }

        // Load existing showTotalSegment setting (default to true for backward compatibility)
        if (quotation.showTotalSegment !== undefined) {
          setShowTotalSegment(quotation.showTotalSegment);
        }

        // Load existing customAdditionalServices data
        if (quotation.customAdditionalServices) {
          setCustomAdditionalServices(quotation.customAdditionalServices);
          formValues.customAdditionalServices = quotation.customAdditionalServices;
        }
      } else if (linkedQuoteRequest) {
        // Creating quotation from quote request - handle customer info mapping
        const customerInfo = mapCustomerInfo(linkedQuoteRequest);
        const parsedProducts = parseProductsFromQuoteRequest(linkedQuoteRequest.products, linkedQuoteRequest.services);

        const formValues = {
          companyName: customerInfo.companyName,
          attn: customerInfo.attn,
          tel: linkedQuoteRequest.customerPhone || '',
          email: linkedQuoteRequest.customerEmail || '',
          siteAddress: linkedQuoteRequest.services?.siteAddress || '',
          quotationType: linkedQuoteRequest.enquiryType === 'purchase'
            ? QUOTATION_TYPES.PURCHASE
            : QUOTATION_TYPES.RENTAL,
          paymentTerms: PAYMENT_TERMS.DEPOSIT_50
        };

        form.setFieldsValue(formValues);
        setQuotationType(linkedQuoteRequest.enquiryType === 'purchase'
          ? QUOTATION_TYPES.PURCHASE
          : QUOTATION_TYPES.RENTAL);

        // Set parsed products as line items with auto-generated descriptions
        if (parsedProducts && parsedProducts.length > 0) {
          const processedLineItems = regenerateAutoDescriptionsOnLoad(parsedProducts);
          setLineItems(processedLineItems);
        }

        // Extract and convert rental period data from quote request
        const quotationType = linkedQuoteRequest.enquiryType === 'purchase'
          ? QUOTATION_TYPES.PURCHASE
          : QUOTATION_TYPES.RENTAL;

        let dateEntries = [];

        if (quotationType === QUOTATION_TYPES.RENTAL) {
          // Handle rental dates from quote request
          if (linkedQuoteRequest.dateRental && Array.isArray(linkedQuoteRequest.dateRental) && linkedQuoteRequest.dateRental.length === 2) {
            // Quote request has specific rental date range
            console.log('🔍 Converting rental date range from quote request:', linkedQuoteRequest.dateRental);
            dateEntries = [{
              id: Date.now(),
              type: 'range',
              value: linkedQuoteRequest.dateRental[0], // Start date in ISO format
              endValue: linkedQuoteRequest.dateRental[1], // End date in ISO format
              duration: null,
              durationUnit: null
            }];
          } else if (linkedQuoteRequest.dateDuration && linkedQuoteRequest.durationType) {
            // Quote request has duration-based rental period
            console.log('🔍 Converting rental duration from quote request:', {
              duration: linkedQuoteRequest.dateDuration,
              durationType: linkedQuoteRequest.durationType
            });
            dateEntries = [{
              id: Date.now(),
              type: 'duration',
              value: null,
              endValue: null,
              duration: linkedQuoteRequest.dateDuration,
              durationUnit: linkedQuoteRequest.durationType === 'day' ? 'days' : 
                           linkedQuoteRequest.durationType === 'week' ? 'weeks' : 
                           linkedQuoteRequest.durationType === 'month' ? 'months' : 'days'
            }];
          } else {
            // No rental period specified, create default
            console.log('🔍 No rental period in quote request, creating default');
            dateEntries = [createEmptyDateEntry(quotationType)];
          }
        } else if (quotationType === QUOTATION_TYPES.PURCHASE) {
          // Handle purchase dates from quote request
          if (linkedQuoteRequest.datePurchase && linkedQuoteRequest.datePurchase !== '') {
            // Quote request has specific delivery date
            console.log('🔍 Converting delivery date from quote request:', linkedQuoteRequest.datePurchase);
            dateEntries = [{
              id: Date.now(),
              type: 'date',
              value: linkedQuoteRequest.datePurchase, // Delivery date in ISO format
              endValue: null,
              duration: null,
              durationUnit: null
            }];
          } else {
            // No delivery date specified, create default
            console.log('🔍 No delivery date in quote request, creating default');
            dateEntries = [createEmptyDateEntry(quotationType)];
          }
        } else {
          // Fallback to default
          dateEntries = [createEmptyDateEntry(quotationType)];
        }

        console.log('✅ Final date entries for quotation:', dateEntries);
        setDateEntries(dateEntries);
      } else {
        // New quotation
        form.setFieldsValue({
          quotationType: QUOTATION_TYPES.PURCHASE,
          paymentTerms: PAYMENT_TERMS.DEPOSIT_50
        });

        // Initialize with default entry for new quotations
        const defaultDates = [createEmptyDateEntry(QUOTATION_TYPES.PURCHASE)];
        setDateEntries(defaultDates);
      }
    };

    const timeoutId = setTimeout(initializeForm, 0);
    return () => clearTimeout(timeoutId);
  }, [quotation, linkedQuoteRequest, form]);

  const handleQuotationTypeChange = (value) => {
    setQuotationType(value);
  };

  const addLineItem = () => {
    const newId = Math.max(...lineItems.map(item => item.id)) + 1;
    const newLineItem = createEnhancedLineItem({ 
      id: newId, 
      description: '', 
      quantity: 1, 
      unitPrice: 0, 
      total: 0,
      workingHeight: undefined,
      width: undefined
    });
    setLineItems([...lineItems, newLineItem]);
  };

  const removeLineItem = useCallback((id) => {
    setLineItems(prevItems => {
      if (prevItems.length > 1) {
        return prevItems.filter(item => item.id !== id);
      }
      return prevItems;
    });
  }, []);

  const updateLineItem = useCallback((id, field, value) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return updateLineItemWithSmartDescription(item, field, value);
      }
      return item;
    }));
  }, []);

  // Function to reset line item description to auto-generated version
  const resetLineItemDescription = useCallback((id) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return resetToAutoDescription(item);
      }
      return item;
    }));
  }, []);

  // Function to accept auto-generated description update
  const acceptDescriptionUpdate = useCallback((id) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          description: item.autoDescription,
          isManuallyEdited: false,
          showUpdatePrompt: false
        };
      }
      return item;
    }));
  }, []);

  // Function to dismiss description update prompt
  const dismissDescriptionUpdate = useCallback((id) => {
    setLineItems(prevItems => prevItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          showUpdatePrompt: false
        };
      }
      return item;
    }));
  }, []);

  const toggleAdditionalService = useCallback((serviceKey) => {
    setAdditionalServices(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        selected: !prev[serviceKey]?.selected
      }
    }));
  }, []);

  const updateAdditionalServicePrice = useCallback((serviceKey, price) => {
    setAdditionalServices(prev => ({
      ...prev,
      [serviceKey]: {
        ...prev[serviceKey],
        price: price || 0
      }
    }));
  }, []);

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const calculateAdditionalServicesTotal = () => {
    return Object.values(additionalServices).reduce((sum, service) => 
      sum + (service.selected ? service.price : 0), 0
    );
  };

  // Calculate GST amount based on subtotal after discount
  const calculateGstAmount = () => {
    if (gst.percentage <= 0) return 0;
    const subtotalAfterDiscount = calculateSubtotal() - calculateDiscountAmount();
    return subtotalAfterDiscount * (gst.percentage / 100);
  };

  // Calculate main total (subtotal - discount + GST) - excludes additional services
  const calculateMainTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();
    const gstAmount = calculateGstAmount();
    return subtotal - discountAmount + gstAmount;
  };



  // Handle multiple dates change
  const handleMultipleDatesChange = useCallback((newDateEntries) => {
    setDateEntries(newDateEntries);
  }, []);

  const handleSubmit = async (values) => {
    // Prevent double submissions
    const currentTime = Date.now();
    const timeSinceLastSubmission = currentTime - lastSubmissionTime;

    // If already submitting or submitted within last 2 seconds, prevent duplicate
    if (isSubmitting || timeSinceLastSubmission < 2000) {
      console.warn('🚫 Duplicate submission prevented:', {
        isSubmitting,
        timeSinceLastSubmission,
        lastSubmissionTime: new Date(lastSubmissionTime).toISOString()
      });
      message.warning('Please wait, your quotation is being saved...');
      return;
    }

    // Set submission flags
    setIsSubmitting(true);
    setLastSubmissionTime(currentTime);
    setLoading(true);

    console.log('✅ Starting quotation submission:', {
      timestamp: new Date(currentTime).toISOString(),
      quotationId: quotation?.id || 'new'
    });

    // Validate required fields before processing
    if (!values.companyName || values.companyName.trim() === '') {
      message.error('Company name is required');
      setLoading(false);
      setIsSubmitting(false);
      return;
    }

    if (!values.attn || values.attn.trim() === '') {
      message.error('Attention person is required');
      setLoading(false);
      setIsSubmitting(false);
      return;
    }

    try {
      // Validate line items - at least one item must have a description
      const validLineItems = lineItems.filter(item => item.description && item.description.trim() !== '');
      if (validLineItems.length === 0) {
        message.error('Please add at least one service/product with a description');
        return;
      }

      // Helper function to clean undefined values
      const cleanValue = (value) => {
        if (value === undefined || value === null) return '';
        if (typeof value === 'string' && value.trim() === '') return '';
        return value;
      };

      // Helper function to clean entire object of undefined values
      const cleanObject = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;

        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = value;
          }
        }
        return cleaned;
      };

      // Helper function to sanitize line items for Firebase
      const sanitizeLineItems = (items) => {
        return items.map(item => {
          const sanitizedItem = {};
          
          // Define allowed fields and their default values
          const allowedFields = {
            id: null,
            description: '',
            quantity: 1,
            unitPrice: 0,
            total: 0,
            isManuallyEdited: false,
            autoDescription: '',
            showUpdatePrompt: false,
            services: {}
          };
          
          // Only include fields that have valid, non-undefined values
          Object.keys(allowedFields).forEach(key => {
            if (item[key] !== undefined && item[key] !== null) {
              sanitizedItem[key] = item[key];
            } else if (allowedFields[key] !== null) {
              // Use default value for required fields
              sanitizedItem[key] = allowedFields[key];
            }
          });
          
          // Only include width and workingHeight if they have valid values
          if (item.width !== undefined && item.width !== null && item.width !== '') {
            sanitizedItem.width = item.width;
          }
          
          if (item.workingHeight !== undefined && item.workingHeight !== null && item.workingHeight !== '') {
            sanitizedItem.workingHeight = item.workingHeight;
          }
          
          return sanitizedItem;
        });
      };

      // Use new unified date system - store dates array
      const { storeUnifiedDates } = require('../../utils/unifiedDateStorage');
      const documentWithDates = storeUnifiedDates({}, dateEntries);

      const quotationData = cleanObject({
        // Customer information
        companyName: cleanValue(values.companyName),
        attn: cleanValue(values.attn),
        tel: cleanValue(values.tel),
        email: cleanValue(values.email),
        siteAddress: cleanValue(values.siteAddress),
        billingAddress: cleanValue(values.billingAddress),

        // Quotation details
        quotationType: values.quotationType,

        // NEW: Unified date system
        ...documentWithDates,

        // Payment and terms
        paymentTerms: values.paymentTerms,
        customPaymentTerms: values.paymentTerms === PAYMENT_TERMS.CUSTOM
          ? cleanValue(values.customPaymentTerms)
          : '',

        // Discount information
        discount: discount || { type: 'none', value: 0 },
        discountAmount: calculateDiscountAmount(),

        // GST information
        gst: gst || { percentage: 0 },
        gstAmount: calculateGstAmount(),

        // Line items and pricing
        lineItems: sanitizeLineItems(lineItems) || [],
        additionalServices: additionalServices || {},
        subtotal: calculateSubtotal(),
        additionalServicesTotal: calculateAdditionalServicesTotal(),
        grandTotal: calculateMainTotal(),

        // Additional information (optional)
        notes: cleanValue(values.notes),
        internalRemarks: cleanValue(values.internalRemarks),
        customAdditionalServices: cleanValue(customAdditionalServices),

        // PDF display options
        showTotalSegment: showTotalSegment,

        // Link to quote request if applicable
        quoteRequestId: linkedQuoteRequest?.id || quotation?.quoteRequestId || ''
      });

      // 🔍 ADD THIS DEBUG LOG
      console.log('🔍 STEP 1 - SAVING: Quotation data being saved:', {
        dates: quotationData.dates,
        quotationType: quotationData.quotationType,
        customAdditionalServices: quotationData.customAdditionalServices,
        customAdditionalServicesState: customAdditionalServices,
        dateEntries: dateEntries,
        fullData: quotationData
      });

      let result;
      if (quotation) {
        // Update existing quotation
        result = await updateQuotation(quotation.id, quotationData);
        if (result) {
          message.success('Quotation updated successfully');
          onSave({ id: quotation.id, ...quotationData });
        }
      } else {
        // Create new quotation
        result = await createQuotation(quotationData);
        message.success('Quotation created successfully');
        onSave(result);
      }
    } catch (error) {
      console.error('❌ Error saving quotation:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });

      // Show more specific error message
      let errorMessage = 'Failed to save quotation. ';
      if (error.message.includes('permission-denied')) {
        errorMessage += 'Permission denied. Please check your access rights.';
      } else if (error.message.includes('network')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('invalid-argument')) {
        errorMessage += 'Invalid data provided. Please check all fields.';
      } else {
        errorMessage += 'Please try again or contact support.';
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      
      console.log('✅ Quotation submission completed:', {
        timestamp: new Date().toISOString(),
        quotationId: quotation?.id || 'new'
      });
    }
  };



  // Calculate discount amount
  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discount.type === 'percentage') {
      return subtotal * (discount.value / 100);
    } else if (discount.type === 'fixed') {
      return discount.value;
    }
    return 0;
  };

  const lineItemColumns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => (
        <div>
          <div style={{ position: 'relative' }}>
            <TextArea
              value={text}
              onChange={(e) => updateLineItem(record.id, 'description', e.target.value)}
              placeholder="Enter service description (multi-line supported)"
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{
                borderColor: record.isManuallyEdited ? '#1890ff' : undefined,
                backgroundColor: record.isManuallyEdited ? '#f6ffed' : undefined
              }}
            />
            {/* Visual indicator for auto-generated vs manual descriptions */}
            {!record.isManuallyEdited && record.autoDescription && (
              <div style={{
                position: 'absolute',
                top: 4,
                right: 4,
                fontSize: '10px',
                color: '#52c41a',
                backgroundColor: 'rgba(82, 196, 26, 0.1)',
                padding: '2px 4px',
                borderRadius: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '2px'
              }}>
                <SyncOutlined style={{ fontSize: '8px' }} />
                Auto
              </div>
            )}
          </div>

          {/* Update prompt for manually edited descriptions */}
          {record.showUpdatePrompt && (
            <div style={{
              marginTop: 4,
              padding: '6px 8px',
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <InfoCircleOutlined style={{ color: '#fa8c16' }} />
                  <span>Update available: "{record.autoDescription}"</span>
                </div>
                <div>
                  <Button
                    size="small"
                    type="text"
                    icon={<CheckOutlined />}
                    onClick={() => acceptDescriptionUpdate(record.id)}
                    style={{ color: '#52c41a', padding: '0 4px' }}
                    title="Accept update"
                  />
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => dismissDescriptionUpdate(record.id)}
                    style={{ color: '#ff4d4f', padding: '0 4px' }}
                    title="Dismiss"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Helper function controls */}
          <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
            <Space wrap>
              <Radio.Group
                size="small"
                value={record.width}
                onChange={(e) => updateLineItem(record.id, 'width', e.target.value)}
              >
                <Radio.Button value="single">Single</Radio.Button>
                <Radio.Button value="double">Double</Radio.Button>
              </Radio.Group>
              <InputNumber
                size="small"
                placeholder="Height"
                value={record.workingHeight}
                onChange={(value) => updateLineItem(record.id, 'workingHeight', value)}
                min={2}
                max={15}
                step={0.1}
                addonAfter="m"
                style={{ width: '100px' }}
              />
              {/* Reset to auto button */}
              {record.isManuallyEdited && record.autoDescription && (
                <Button
                  size="small"
                  type="text"
                  icon={<SyncOutlined />}
                  onClick={() => resetLineItemDescription(record.id)}
                  title="Reset to auto-generated description"
                  style={{ color: '#1890ff' }}
                >
                  Reset
                </Button>
              )}
            </Space>
            {/* Service checkboxes */}
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <Checkbox
                size="small"
                checked={record.services?.transportation || false}
                onChange={(e) => updateLineItem(record.id, 'services', { transportation: e.target.checked })}
                style={{ fontSize: '11px' }}
              >
                Transportation
              </Checkbox>
              <Checkbox
                size="small"
                checked={record.services?.assembly || false}
                onChange={(e) => updateLineItem(record.id, 'services', { assembly: e.target.checked })}
                style={{ fontSize: '11px' }}
              >
                Assembly & dismantling
              </Checkbox>
              <Checkbox
                size="small"
                checked={record.services?.endorsement || false}
                onChange={(e) => updateLineItem(record.id, 'services', { endorsement: e.target.checked })}
                style={{ fontSize: '11px' }}
              >
                Weekly inspection
              </Checkbox>
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (text, record) => (
        <InputNumber
          min={1}
          value={text}
          onChange={(value) => updateLineItem(record.id, 'quantity', value)}
        />
      )
    },
    {
      title: 'Unit Price (SGD)',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 150,
      render: (text, record) => (
        <InputNumber
          min={0}
          precision={2}
          value={text}
          onChange={(value) => updateLineItem(record.id, 'unitPrice', value)}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
        />
      )
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 150,
      render: (text) => `$${(text || 0).toFixed(2)}`
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeLineItem(record.id)}
          disabled={lineItems.length === 1}
        />
      )
    }
  ];

  return (
    <div className="admin-form-container" style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3}>
            {quotation ? 'Edit Quotation' : 'Create New Quotation'}
          </Title>
          {quotation && (
            <Text type="secondary">
              Quotation Number: {quotation.quotationNumber}
            </Text>
          )}
        </div>

        <Form
          key={formKey}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            quotationType: QUOTATION_TYPES.PURCHASE,
            paymentTerms: PAYMENT_TERMS.DEPOSIT_50
          }}
        >
          {/* Customer Information Section */}
          <Card title="Customer Information" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Company Name"
                  name="companyName"
                  rules={[{ required: true, message: 'Please enter company name' }]}
                >
                  <Input placeholder="Enter company name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Attention To"
                  name="attn"
                  rules={[{ required: true, message: 'Please enter contact person' }]}
                >
                  <Input placeholder="Enter contact person name" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Telephone"
                  name="tel"
                  rules={[{ required: true, message: 'Please enter telephone number' }]}
                >
                  <Input placeholder="Enter telephone number" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input placeholder="Enter email address (optional - will show as NA if not provided)" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Site Address"
                  name="siteAddress"
                >
                  <TextArea 
                    rows={2} 
                    placeholder="Enter the site address where services will be provided (optional)" 
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="Billing Address (Optional)"
                  name="billingAddress"
                >
                  <TextArea 
                    rows={2} 
                    placeholder="Enter billing address if different from site address (optional)" 
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Quotation Details Section */}
          <Card title="Quotation Details" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Quotation Type"
                  name="quotationType"
                  rules={[{ required: true, message: 'Please select quotation type' }]}
                >
                  <Select onChange={handleQuotationTypeChange}>
                    {Object.entries(QUOTATION_TYPE_LABELS).map(([key, label]) => (
                      <Option key={key} value={key}>{label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <UnifiedDateSection
                  documentType={quotationType}
                  dates={dateEntries}
                  onDatesChange={handleMultipleDatesChange}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Payment Terms"
                  name="paymentTerms"
                  rules={[{ required: true, message: 'Please select payment terms' }]}
                >
                  <Select>
                    <Option value={PAYMENT_TERMS.DEPOSIT_50_RENTAL}>50% deposit upon confirmation, balance on the first day of rental</Option>
                    <Option value={PAYMENT_TERMS.DEPOSIT_50_DELIVERY}>50% deposit upon confirmation, balance on the day of delivery</Option>
                    <Option value={PAYMENT_TERMS.FULL_PAYMENT}>100% payment due</Option>
                    <Option value={PAYMENT_TERMS.CUSTOM}>Custom terms</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.paymentTerms !== currentValues.paymentTerms
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('paymentTerms') === PAYMENT_TERMS.CUSTOM ? (
                      <Form.Item
                        label="Custom Payment Terms"
                        name="customPaymentTerms"
                        rules={[{ required: true, message: 'Please enter custom payment terms' }]}
                      >
                        <TextArea rows={2} placeholder="Enter custom payment terms" />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Discount Section */}
          <Card title="Discount (Optional)" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Discount Type">
                  <Select 
                    value={discount.type} 
                    onChange={(value) => setDiscount({ type: value, value: 0 })}
                  >
                    <Option value="none">No Discount</Option>
                    <Option value="percentage">Percentage (%)</Option>
                    <Option value="fixed">Fixed Amount (SGD)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                {discount.type !== 'none' && (
                  <Form.Item label={discount.type === 'percentage' ? 'Percentage' : 'Amount'}>
                    <InputNumber
                      min={0}
                      max={discount.type === 'percentage' ? 100 : undefined}
                      precision={discount.type === 'percentage' ? 2 : 2}
                      value={discount.value}
                      onChange={(value) => setDiscount({ ...discount, value: value || 0 })}
                      style={{ width: '100%' }}
                      formatter={discount.type === 'percentage' 
                        ? (value) => `${value}%`
                        : (value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      }
                      parser={discount.type === 'percentage'
                        ? (value) => value.replace('%', '')
                        : (value) => value.replace(/\$\s?|(,*)/g, '')
                      }
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={8}>
                {discount.type !== 'none' && (
                  <Form.Item label="Discount Amount">
                    <Text strong style={{ color: '#52c41a' }}>
                      -${calculateDiscountAmount().toFixed(2)}
                    </Text>
                  </Form.Item>
                )}
              </Col>
            </Row>
          </Card>

          {/* GST Section */}
          <Card title="GST (Optional)" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="GST Percentage">
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    value={gst.percentage}
                    onChange={(value) => setGst({ percentage: value || 0 })}
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace('%', '')}
                    placeholder="0.00"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                {gst.percentage > 0 && (
                  <Form.Item label="GST Amount">
                    <Text strong style={{ color: '#1890ff' }}>
                      +${calculateGstAmount().toFixed(2)}
                    </Text>
                  </Form.Item>
                )}
              </Col>
              <Col span={8}>
                {gst.percentage > 0 && (
                  <Form.Item label="Applied On">
                    <Text type="secondary">
                      Subtotal after discount
                    </Text>
                  </Form.Item>
                )}
              </Col>
            </Row>
          </Card>


          {/* Line Items Section */}
          <Card 
            title="Services/Products" 
            style={{ marginBottom: 16 }}
            extra={
              <Button 
                type="dashed" 
                icon={<PlusOutlined />} 
                onClick={addLineItem}
              >
                Add Item
              </Button>
            }
          >
            {/* Conditional rendering: Mobile layout or Table */}
            {isMobile ? (
              <MobileLineItems 
                lineItems={lineItems}
                onUpdate={updateLineItem}
                onRemove={removeLineItem}
                onResetDescription={resetLineItemDescription}
                onAcceptUpdate={acceptDescriptionUpdate}
                onDismissUpdate={dismissDescriptionUpdate}
              />
            ) : (
              <Table
                dataSource={lineItems}
                columns={lineItemColumns}
                pagination={false}
                rowKey="id"
                size="small"
                className={isMobile ? 'mobile-hidden' : ''}
              />
            )}
            
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <div>
                <Text strong>Subtotal: ${calculateSubtotal().toFixed(2)}</Text>
              </div>
              {discount.type !== 'none' && discount.value > 0 && (
                <div style={{ marginTop: 4 }}>
                  <Text style={{ color: '#52c41a' }}>
                    Discount: -${calculateDiscountAmount().toFixed(2)}
                  </Text>
                </div>
              )}
              {gst.percentage > 0 && (
                <div style={{ marginTop: 4 }}>
                  <Text style={{ color: '#1890ff' }}>
                    GST ({gst.percentage}%): +${calculateGstAmount().toFixed(2)}
                  </Text>
                </div>
              )}
              <div style={{ marginTop: 8, paddingTop: 4, borderTop: '1px solid #d9d9d9' }}>
                <Text strong style={{ fontSize: '16px' }}>
                  TOTAL (SGD): ${calculateMainTotal().toFixed(2)}
                </Text>
              </div>
              <div style={{ marginTop: 12, textAlign: 'left' }}>
                <Checkbox
                  checked={showTotalSegment}
                  onChange={(e) => setShowTotalSegment(e.target.checked)}
                  style={{ fontSize: '14px' }}
                >
                  <Text strong>Show Total Pricing Details</Text>
                </Checkbox>
                <div style={{ 
                  marginTop: 4, 
                  fontSize: '12px', 
                  color: '#666',
                  marginLeft: '24px'
                }}>
                  When checked, the PDF will include subtotal, discount, GST, and total amounts
                </div>
              </div>
            </div>
          </Card>

          {/* Additional Services Section */}
          <AdditionalServicesSection
            additionalServices={additionalServices}
            onToggleService={toggleAdditionalService}
            onUpdateServicePrice={updateAdditionalServicePrice}
            customAdditionalServices={customAdditionalServices}
            onCustomAdditionalServicesChange={setCustomAdditionalServices}
          />

          {/* Notes Section */}
          <Card title="Additional Notes" style={{ marginBottom: 16 }}>
            <Form.Item
              label="Notes"
              name="notes"
            >
              <TextArea
                rows={4}
                placeholder="Enter any additional notes or terms..."
              />
            </Form.Item>
          </Card>

          {/* Internal Remarks Section */}
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#1890ff' }}>🔒</span>
                <span>Internal Remarks (Admin Only)</span>
              </div>
            } 
            style={{ 
              marginBottom: 24,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f'
            }}
          >
            <div style={{ 
              marginBottom: 8, 
              fontSize: '12px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              Private notes for internal reference only. This will not appear in PDFs or client communications.
            </div>
            <Form.Item
              label="Internal Remarks"
              name="internalRemarks"
            >
              <TextArea
                rows={3}
                placeholder="Private notes for internal reference (e.g., client requested clean scaffold, special delivery instructions, follow-up reminders, etc.)"
                style={{ backgroundColor: '#fff' }}
              />
            </Form.Item>
          </Card>

          {/* Action Buttons */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Space size="middle">
              <Button onClick={onCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                htmlType="submit" 
                loading={loading}
              >
                Save Quotation
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default QuotationForm;
